import { useEffect, useMemo, useState } from "react"
import { Link, Navigate, useNavigate } from "react-router-dom"
import { AlertTriangle, Car, ClipboardList, Plus, Search, Share2, Stethoscope } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/PageHeader"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/contexts/AuthContext"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { toast } from "sonner"

interface DiagnosticoResumo {
  id: string
  titulo: string | null
  status: string
  share_token: string | null
  cliente_ciente_em: string | null
  cliente_autorizou_em: string | null
  created_at: string
  updated_at: string
  veiculos: {
    id: string
    placa: string
    marca: string
    modelo: string
    ano: number
  } | null
  diagnosticos_itens: Array<{ urgencia: string }>
}

const Diagnosticos = () => {
  const navigate = useNavigate()
  const { loja } = useAuth()
  const vertical = useTipoOperacao()
  const [diagnosticos, setDiagnosticos] = useState<DiagnosticoResumo[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")

  useEffect(() => {
    if (vertical.tipo !== "oficina" || !loja?.id) return
    void fetchDiagnosticos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loja?.id, vertical.tipo])

  const fetchDiagnosticos = async () => {
    try {
      const { data, error } = await supabase
        .from("diagnosticos")
        .select(`
          id,
          titulo,
          status,
          share_token,
          cliente_ciente_em,
          cliente_autorizou_em,
          created_at,
          updated_at,
          veiculos:veiculo_id (
            id,
            placa,
            marca,
            modelo,
            ano
          ),
          diagnosticos_itens (
            urgencia
          )
        `)
        .eq("loja_id", loja!.id)
        .order("updated_at", { ascending: false })

      if (error) {
        console.error("Erro ao carregar diagnósticos:", error)
        toast.error("Erro ao carregar diagnósticos")
        return
      }

      setDiagnosticos((data ?? []) as DiagnosticoResumo[])
    } catch (error) {
      console.error("Erro inesperado ao carregar diagnósticos:", error)
      toast.error("Erro inesperado ao carregar diagnósticos")
    } finally {
      setLoading(false)
    }
  }

  const diagnosticosFiltrados = useMemo(() => {
    const termo = searchTerm.trim().toLowerCase()
    if (!termo) return diagnosticos

    return diagnosticos.filter((diagnostico) => {
      const veiculo = diagnostico.veiculos
      return [
        diagnostico.titulo,
        veiculo?.placa,
        veiculo?.marca,
        veiculo?.modelo,
        veiculo?.ano ? String(veiculo.ano) : null,
      ]
        .filter(Boolean)
        .some((valor) => String(valor).toLowerCase().includes(termo))
    })
  }, [diagnosticos, searchTerm])

  if (vertical.tipo !== "oficina") {
    return <Navigate to="/dashboard" replace />
  }

  const totalCompartilhados = diagnosticos.filter((d) => d.status === "compartilhado" || d.share_token).length
  const totalAutorizados = diagnosticos.filter((d) => d.status === "autorizado" || d.cliente_autorizou_em).length

  return (
    <div className="space-y-6">
      <PageHeader
        title="Diagnósticos"
        description="Acompanhe problemas encontrados, recomendações e links enviados aos clientes."
      >
        <Button asChild>
          <Link to="/veiculos">
            <Plus className="mr-2 h-4 w-4" />
            Novo diagnóstico
          </Link>
        </Button>
      </PageHeader>

      <div className="grid gap-3 md:grid-cols-3">
        <Card className="border-border/70">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground">Total</p>
            <p className="mt-1 font-data text-2xl font-semibold">{diagnosticos.length}</p>
          </CardContent>
        </Card>
        <Card className="border-border/70">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground">Compartilhados</p>
            <p className="mt-1 font-data text-2xl font-semibold">{totalCompartilhados}</p>
          </CardContent>
        </Card>
        <Card className="border-border/70">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground">Autorizados</p>
            <p className="mt-1 font-data text-2xl font-semibold text-green-700">{totalAutorizados}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por placa, veículo ou título"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      ) : diagnosticosFiltrados.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <Stethoscope className="h-10 w-10 text-muted-foreground opacity-30" />
            <div>
              <p className="font-medium text-foreground">
                {diagnosticos.length === 0 ? "Nenhum diagnóstico registrado" : "Nenhum diagnóstico encontrado"}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Comece pelo veículo e registre os problemas encontrados durante o atendimento.
              </p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/veiculos">Escolher veículo</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {diagnosticosFiltrados.map((diagnostico) => {
            const veiculo = diagnostico.veiculos
            const totalItens = diagnostico.diagnosticos_itens.length
            const urgentes = diagnostico.diagnosticos_itens.filter((item) => item.urgencia === "urgente").length
            const compartilhado = diagnostico.status === "compartilhado" || Boolean(diagnostico.share_token)
            const autorizado = diagnostico.status === "autorizado" || Boolean(diagnostico.cliente_autorizou_em)
            const ciente = diagnostico.status === "ciente" || Boolean(diagnostico.cliente_ciente_em)

            return (
              <Card
                key={diagnostico.id}
                className="cursor-pointer border-border/70 transition-colors hover:bg-muted/30"
                onClick={() => navigate(`/diagnostico/${diagnostico.id}`)}
              >
                <CardContent className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-base font-semibold">
                        {diagnostico.titulo || "Diagnóstico técnico"}
                      </h2>
                      {compartilhado && (
                        <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">
                          <Share2 className="mr-1 h-3 w-3" />
                          Compartilhado
                        </Badge>
                      )}
                      {autorizado ? (
                        <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                          Autorizado
                        </Badge>
                      ) : ciente ? (
                        <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                          Cliente ciente
                        </Badge>
                      ) : null}
                      {urgentes > 0 && (
                        <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
                          <AlertTriangle className="mr-1 h-3 w-3" />
                          {urgentes} urgente{urgentes > 1 ? "s" : ""}
                        </Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      {veiculo ? (
                        <span className="flex items-center gap-1.5">
                          <Car className="h-3.5 w-3.5" />
                          <span className="font-mono font-semibold text-foreground">{veiculo.placa}</span>
                          {veiculo.marca} {veiculo.modelo} {veiculo.ano}
                        </span>
                      ) : (
                        <span>Veículo não encontrado</span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <ClipboardList className="h-3.5 w-3.5" />
                        {totalItens} {totalItens === 1 ? "item" : "itens"}
                      </span>
                    </div>
                  </div>

                  <div className="text-left text-xs text-muted-foreground md:text-right">
                    <p>Atualizado em</p>
                    <p className="font-medium text-foreground">
                      {new Date(diagnostico.updated_at || diagnostico.created_at).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Diagnosticos
